import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import QuestionLayout from '../common/QuestionLayout';
import {DeviceType} from '../../../../../incase-domain/common/DeviceType.enum';
import QuestionAnswer, {AnswerTypes} from '../question/QuestionAnswer';
import {getAnswerStyles} from '../common/getStyles';
import {QuestionElementInteractions} from '../../../../../incase-domain/proptypes/runfile';
import {IElementQuestionYesNoV2} from '../../../../../incase-domain/elements/question/questionYesNoV2';
import {activeMobileQuestionSelector, loadedPresentationSelector} from '../../../../../store/selectors/presentation.selectors';
import {fetchQuestionReportAction} from '../../../../../store/actions/analytics.actions';
import {handleSelectAnswer} from '../utils/handleTryButton.question.utils';
import useQuestionTry from '../useQuestionTry';
import {useOpenComment} from '../common/useOpenComment';
import {usePlayerLabels} from '../../../../../hooks/usePlayerLabels';

function answerLabelCode(text: string): 'elements-yesnoquestion-yes' | 'elements-yesnoquestion-no' {
    return text.toLowerCase() === 'no' ? 'elements-yesnoquestion-no' : 'elements-yesnoquestion-yes';
}

function answerTextKey(text: string): 'common.yes' | 'common.no' {
    return text.toLowerCase() === 'no' ? 'common.no' : 'common.yes';
}

interface YesNoQuestionElementV2Props {
    element: IElementQuestionYesNoV2;
    savePresentationRunFile: (elementRunFile: QuestionElementInteractions, newPoints?: number) => void;
    device?: DeviceType;
    elementInteractions: QuestionElementInteractions;
}

function YesNoQuestionElementV2({
    element,
    elementInteractions,
    device = DeviceType.DESKTOP,
    savePresentationRunFile,
}: YesNoQuestionElementV2Props): JSX.Element {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const getLabel = usePlayerLabels();
    const handleTry = useQuestionTry<IElementQuestionYesNoV2>();
    const presentation = useSelector(loadedPresentationSelector);
    const activeMobileQuestion = useSelector(activeMobileQuestionSelector);

    const {getCommentProps, toggleComment} = useOpenComment();
    const markedAnswers = elementInteractions.markedAnswers ?? [];
    const {answered} = elementInteractions;

    useEffect(() => {
        if (element.showResults) {
            dispatch(fetchQuestionReportAction(element.id));
        }
    }, [dispatch, element.id, element.showResults, answered]);

    function handleAnswerClick(index: number): void {
        handleSelectAnswer(elementInteractions, element, index, markedAnswers, savePresentationRunFile, presentation);
    }

    const isAnswerSelected = (index: number): boolean =>
        !!elementInteractions.markedAnswers?.includes(index) || !!elementInteractions.clickedAnswers?.includes(index);

    const getAnswerType = (index: number): AnswerTypes => {
        if (elementInteractions.answered && isAnswerSelected(index)) {
            return element.answers[index].type as AnswerTypes;
        }
        return isAnswerSelected(index) ? AnswerTypes.SELECTED : AnswerTypes.NONE;
    };

    return (
        <QuestionLayout
            element={element}
            elementInteractions={elementInteractions}
            device={device}
            showConfirmButton
            onConfirm={() => handleTry(element, elementInteractions)}
            confirmDisabled={markedAnswers.length === 0}
        >
            <div
                className={`c-yesNoQuestion ${device === DeviceType.MOBILE && 'c-yesNoQuestion--mobile'} ${
                    elementInteractions.answered && 'c-yesNoQuestion--answered'
                } ${activeMobileQuestion === element.id && 'is-active'}`}
            >
                {element.answers.map((answer, index) => (
                    <QuestionAnswer
                        key={`yes-no-question-answer-${element.id}-${index}`}
                        answer={getLabel(answerLabelCode(answer.text)) || t(answerTextKey(answer.text))}
                        answerType={getAnswerType(index)}
                        comment={
                            elementInteractions.answered && (isAnswerSelected(index) || element.showAllComments)
                                ? answer.comment
                                : undefined
                        }
                        correctColor={element.correctColor}
                        incorrectColor={element.incorrectColor}
                        debatableColor={element.debatableColor}
                        styles={getAnswerStyles(device, element)}
                        onClick={() => {
                            handleAnswerClick(index);
                            toggleComment(index, !!answer.comment && answered && (isAnswerSelected(index) || element.showAllComments));
                        }}
                        writeAnswerIndices={element.writeAnswerIndices}
                        index={index}
                        device={device}
                        {...getCommentProps(index, !!answer.comment && answered && (isAnswerSelected(index) || element.showAllComments))}
                    />
                ))}
            </div>
        </QuestionLayout>
    );
}

export default YesNoQuestionElementV2;
